import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X } from 'lucide-react';
import { COMPANY } from '../data/content';

const NAV_LINKS = [
  { name: 'About', href: '/about' },
  { name: 'Platforms', href: '/platforms' },
  { name: 'Leadership', href: '/leadership' },
  { name: 'Media', href: '/media' },
  { name: 'Careers', href: '/careers' },
  { name: 'Contact', href: '/contact' },
];

const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  return (
    <header
      data-testid="main-navbar"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 backdrop-blur-2xl ${
        scrolled ? 'bg-black/80 border-b border-white/10' : 'bg-black/60 border-b border-white/5'
      }`}
    >
      <nav className="max-w-7xl mx-auto px-6 lg:px-10 py-4">
        <div className="flex items-center justify-between">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-3 group" data-testid="navbar-logo">
            <div className="relative">
              <div className="w-9 h-9 rounded-lg overflow-hidden bg-white flex items-center justify-center">
                <img
                  src={COMPANY.logoUrl}
                  alt="Bronto Platforms"
                  className="w-full h-full object-cover"
                />
              </div>
            </div>
            <span className="text-base font-bold tracking-tight rainbow-text">
              {COMPANY.displayName}
            </span>
          </Link>

          {/* Desktop Nav */}
          <div className="hidden lg:flex items-center gap-1">
            {NAV_LINKS.map((link) => {
              const active = location.pathname === link.href;
              return (
                <Link
                  key={link.href}
                  to={link.href}
                  data-testid={`nav-link-${link.name.toLowerCase()}`}
                  className={`px-4 py-2 text-sm font-medium rounded-full transition-all duration-300 ${
                    active
                      ? 'text-white bg-white/10'
                      : 'text-white/65 hover:text-white hover:bg-white/5'
                  }`}
                >
                  {link.name}
                </Link>
              );
            })}
          </div>

          {/* CTA */}
          <div className="hidden lg:flex items-center gap-3">
            <Link
              to="/platforms"
              data-testid="nav-cta-platforms"
              className="text-sm font-medium px-5 py-2.5 rounded-full bg-white text-black hover:bg-white/90 transition-all duration-300"
            >
              View Platforms
            </Link>
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setMobileOpen(!mobileOpen)}
            className="lg:hidden p-2 text-white"
            data-testid="mobile-menu-toggle"
          >
            {mobileOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>

        {/* Mobile Menu */}
        {mobileOpen && (
          <div className="lg:hidden mt-4 pb-4 border-t border-white/5 pt-4" data-testid="mobile-menu">
            <div className="flex flex-col gap-1">
              {NAV_LINKS.map((link) => (
                <Link
                  key={link.href}
                  to={link.href}
                  className="px-4 py-3 text-sm font-medium text-white/80 hover:text-white hover:bg-white/5 rounded-lg transition-all"
                >
                  {link.name}
                </Link>
              ))}
              <Link
                to="/platforms"
                className="mt-2 px-4 py-3 text-sm font-medium bg-white text-black rounded-full text-center"
              >
                View Platforms
              </Link>
            </div>
          </div>
        )}
      </nav>
    </header>
  );
};

export default Navbar;
